import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Stack,
  IconButton,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MathEditor from './MathEditor';
import OptionalImageField from '../common/OptionalImageField';
import AnswerEvaluationFields from './AnswerEvaluationFields';

const QUESTION_TYPES = [
  { value: 'MULTIPLE', label: 'Multiple Choice' },
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'SHORT_ANSWER', label: 'Short Answer' }
];

const SubQuestionDialog = ({
  open,
  onClose,
  subQuestions = [],
  onSave,
}) => {
  const [localSubQuestions, setLocalSubQuestions] = useState([]);
  const [newImage, setNewImage] = useState({});
  const [newImagePreview, setNewImagePreview] = useState({});
  const [newDetailImage, setNewDetailImage] = useState({});
  const [newDetailImagePreview, setNewDetailImagePreview] = useState({});
  const prevOpenRef = useRef(false);

  /** Normalize options from API (array) or form (string) to a single string for display/edit. */
  const optionsToDisplayValue = useCallback((options) => {
    if (options == null) return '';
    if (Array.isArray(options)) {
      return options.map((o) => (o == null ? '' : String(o))).join(', ');
    }
    return typeof options === 'string' ? options : String(options);
  }, []);

  const normalizeSubQuestions = useCallback(() => {
    return subQuestions.length > 0
      ? subQuestions.map((sq) => ({
          ...sq,
          options: optionsToDisplayValue(sq.options),
          answer_evaluation: sq.answer_evaluation || {},
          correct_answer: typeof sq.correct_answer === 'string' ? sq.correct_answer : (sq.correct_answer != null ? String(sq.correct_answer) : ''),
          detail_answer: typeof sq.detail_answer === 'string' ? sq.detail_answer : (sq.detail_answer != null ? String(sq.detail_answer) : ''),
          question_text: typeof sq.question_text === 'string' ? sq.question_text : (sq.question_text != null ? String(sq.question_text) : '')
        }))
      : [{
          question_text: '',
          question_type: 'MULTIPLE',
          options: '',
          correct_answer: '',
          detail_answer: '',
          detail_answer_image: '',
          answer_evaluation: {},
          marks: 1,
          order: 1,
          is_active: true,
          image_url: ''
        }];
  }, [subQuestions, optionsToDisplayValue]);

  // Sync from props: full reset when dialog just opened; only sync list when subQuestions changed while open (e.g. after our onSave) so Remove Image state is not overwritten
  useEffect(() => {
    const dialogJustOpened = open && !prevOpenRef.current;
    if (open) {
      const normalized = normalizeSubQuestions();
      setLocalSubQuestions(normalized);
      if (dialogJustOpened) {
        setNewImage({});
        setNewImagePreview({});
        setNewDetailImage({});
        setNewDetailImagePreview({});
      }
    }
    prevOpenRef.current = open;
  }, [open, subQuestions, normalizeSubQuestions]);

  const handleAddSubQuestion = () => {
    const newSubQuestion = {
      question_text: '',
      question_type: 'MULTIPLE',
      options: '',
      correct_answer: '',
      detail_answer: '',
      detail_answer_image: '',
      answer_evaluation: {},
      marks: 1,
      order: localSubQuestions.length + 1,
      is_active: true,
      image_url: ''
    };
    setLocalSubQuestions([...localSubQuestions, newSubQuestion]);
  };

  const handleRemoveSubQuestion = (index) => {
    const updated = localSubQuestions.filter((_, i) => i !== index);
    // Reorder remaining sub-questions
    const reordered = updated.map((sq, idx) => ({
      ...sq,
      order: idx + 1
    }));
    setLocalSubQuestions(reordered);
    // Clean up image previews
    if (newImagePreview[index]) {
      URL.revokeObjectURL(newImagePreview[index]);
    }
    const newPreviews = { ...newImagePreview };
    delete newPreviews[index];
    setNewImagePreview(newPreviews);
    const newImages = { ...newImage };
    delete newImages[index];
    setNewImage(newImages);
    if (newDetailImagePreview[index]) {
      URL.revokeObjectURL(newDetailImagePreview[index]);
    }
    const newDetailPreviews = { ...newDetailImagePreview };
    delete newDetailPreviews[index];
    setNewDetailImagePreview(newDetailPreviews);
    const newDetailImages = { ...newDetailImage };
    delete newDetailImages[index];
    setNewDetailImage(newDetailImages);
  };

  const handleSubQuestionChange = (index, field, value) => {
    const updated = [...localSubQuestions];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-set options for TRUE_FALSE
    if (field === 'question_type' && value === 'TRUE_FALSE') {
      updated[index].options = '["True", "False"]';
    }

    setLocalSubQuestions(updated);

    if (onSave) {
      onSave(
        updated.map((sq, idx) => ({ ...sq, order: idx + 1 })),
        newImage,
        newDetailImage
      );
    }
  };

  const handleImageChange = (index, file) => {
    if (file) {
      const preview = URL.createObjectURL(file);
      setNewImage(prev => ({ ...prev, [index]: file }));
      setNewImagePreview(prev => ({ ...prev, [index]: preview }));

      // Auto-save after image is set
      const updated = [...localSubQuestions];
      updated[index] = { ...updated[index], image_url: preview };
      setLocalSubQuestions(updated);

      if (onSave) {
        onSave(
          updated.map((sq, idx) => ({ ...sq, order: idx + 1 })),
          { ...newImage, [index]: file },
          newDetailImage
        );
      }
    }
  };

  const handleClearImage = (index) => {
    if (newImagePreview[index]) {
      URL.revokeObjectURL(newImagePreview[index]);
    }
    const newPreviews = { ...newImagePreview };
    delete newPreviews[index];
    setNewImagePreview(newPreviews);
    const newImages = { ...newImage };
    delete newImages[index];
    setNewImage(newImages);

    const updated = [...localSubQuestions];
    updated[index] = { ...updated[index], image_url: '', image_proxy_url: '' };
    setLocalSubQuestions(updated);

    if (onSave) {
      const cleared = { ...newImage };
      delete cleared[index];
      onSave(
        updated.map((sq, idx) => ({ ...sq, order: idx + 1 })),
        cleared,
        newDetailImage
      );
    }
  };

  const handleDetailImageChange = (index, file) => {
    if (file) {
      const preview = URL.createObjectURL(file);
      setNewDetailImage(prev => ({ ...prev, [index]: file }));
      setNewDetailImagePreview(prev => ({ ...prev, [index]: preview }));

      // Auto-save after image is set
      const updated = [...localSubQuestions];
      updated[index] = { ...updated[index], detail_answer_image: preview };
      setLocalSubQuestions(updated);

      if (onSave) {
        onSave(
          updated.map((sq, idx) => ({ ...sq, order: idx + 1 })),
          newImage,
          { ...newDetailImage, [index]: file }
        );
      }
    }
  };

  const handleClearDetailImage = (index) => {
    if (newDetailImagePreview[index]) {
      URL.revokeObjectURL(newDetailImagePreview[index]);
    }
    const newPreviews = { ...newDetailImagePreview };
    delete newPreviews[index];
    setNewDetailImagePreview(newPreviews);
    const newDetailImages = { ...newDetailImage };
    delete newDetailImages[index];
    setNewDetailImage(newDetailImages);

    const updated = [...localSubQuestions];
    updated[index] = { ...updated[index], detail_answer_image: '', detail_answer_image_proxy_url: '' };
    setLocalSubQuestions(updated);

    if (onSave) {
      onSave(
        updated.map((sq, idx) => ({ ...sq, order: idx + 1 })),
        newImage,
        newDetailImages
      );
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(localSubQuestions, newImage, newDetailImage);
    }
    onClose();
  };

  const handleClose = () => {
    // Clean up image previews
    Object.values(newImagePreview).forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    Object.values(newDetailImagePreview).forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    onClose();
  };

  const parseOptions = (options) => {
    if (options == null) return [];
    if (Array.isArray(options)) {
      return options.map((o) => (o == null ? '' : String(o)));
    }
    const text = String(options);
    const trimmed = text.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed.map((o)=>String(o)) : [];
    } catch {
      return trimmed.split(',').map(s => s.trim()).filter(Boolean);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        Manage Sub-Questions
        <IconButton 
          sx={{ position: 'absolute', right: 8, top: 8 }} 
          onClick={handleClose}
          aria-label="Close dialog"
        >
          <CloseIcon/>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Add sub-questions (a, b, c, d, e...). Changes are auto-saved.
        </Typography>
        
        <Stack spacing={3}>
          {localSubQuestions.map((sq, index) => (
            <Paper key={index} elevation={1} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {String.fromCharCode(97 + index)}. Sub-Question {index + 1}
                </Typography>
                {localSubQuestions.length > 1 && (
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleRemoveSubQuestion(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
              <Stack spacing={2}>
                <MathEditor
                  value={sq.question_text || ''}
                  onChange={(value) => handleSubQuestionChange(index, 'question_text', value)}
                  label={`Sub-Question Text *`}
                  placeholder="Enter sub-question text..."
                  error={!sq.question_text || !sq.question_text.trim()}
                />
                
                    {/* Image Upload for Sub-Question - same component as main question and detail answer */}
                <OptionalImageField
                  label="Image (optional)"
                  previewUrl={newImagePreview[index] || sq.image_proxy_url || sq.image_url || ''}
                  onClear={() => handleClearImage(index)}
                  onFileSelect={(file) => handleImageChange(index, file)}
                  onPaste={(file) => handleImageChange(index, file)}
                  minHeight={120}
                  maxImageHeight={150}
                  inputId={`subquestion-image-${index}`}
                  emptyInstruction="Click, paste (Ctrl+V), or attach image here"
                  imageAlt={`sub-question-${index}`}
                />
                
                <TextField
                  select
                  label="Type *"
                  value={sq.question_type || 'MULTIPLE'}
                  onChange={(e) => handleSubQuestionChange(index, 'question_type', e.target.value)}
                  fullWidth
                  size="small"
                >
                  {QUESTION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
                
                {sq.question_type === 'MULTIPLE' && (
                  <MathEditor
                    value={optionsToDisplayValue(sq.options)}
                    onChange={(value) => handleSubQuestionChange(index, 'options', value)}
                    label="Options (comma separated) *"
                    placeholder="Enter options separated by commas..."
                    minRows={2}
                    error={!optionsToDisplayValue(sq.options).trim()}
                  />
                )}
                
                {sq.question_type === 'TRUE_FALSE' ? (
                  <TextField
                    select
                    label="Correct Answer"
                    value={sq.correct_answer || ''}
                    onChange={(e) => handleSubQuestionChange(index, 'correct_answer', e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="True">True</MenuItem>
                    <MenuItem value="False">False</MenuItem>
                  </TextField>
                ) : sq.question_type === 'MULTIPLE' ? (
                  (() => {
                    const opts = parseOptions(sq.options).map((o) => String(o).trim()).filter(Boolean);
                    const current = typeof sq.correct_answer === 'string' ? sq.correct_answer.trim() : (sq.correct_answer != null ? String(sq.correct_answer).trim() : '');
                    const isNotAnOption = Boolean(current) && !opts.includes(current);
                    return (
                      <TextField
                        select
                        label="Correct Answer"
                        value={current}
                        onChange={(e) => handleSubQuestionChange(index, 'correct_answer', e.target.value)}
                        fullWidth
                        size="small"
                        disabled={opts.length === 0}
                        error={isNotAnOption}
                        helperText={
                          opts.length === 0
                            ? 'Add options above first, then select the correct answer'
                            : isNotAnOption
                              ? `"${current}" is not one of the options — select a valid option`
                              : 'Select the option that is the correct answer (optional)'
                        }
                      >
                        <MenuItem value="">None</MenuItem>
                        {isNotAnOption && (
                          <MenuItem value={current}>{current} (not in options)</MenuItem>
                        )}
                        {opts.map((opt, i) => (
                          <MenuItem key={`${opt}-${i}`} value={opt}>{opt}</MenuItem>
                        ))}
                      </TextField>
                    );
                  })()
                ) : (
                  <MathEditor
                    value={typeof sq.correct_answer === 'string' ? sq.correct_answer : (sq.correct_answer != null ? String(sq.correct_answer) : '')}
                    onChange={(value) => handleSubQuestionChange(index, 'correct_answer', value)}
                    label="Correct Answer"
                    placeholder="Enter the correct answer (optional)"
                    minRows={1}
                  />
                )}
                <AnswerEvaluationFields
                  questionType={sq.question_type}
                  value={sq.answer_evaluation}
                  onChange={(value) => handleSubQuestionChange(index, 'answer_evaluation', value)}
                />

                <MathEditor
                  value={typeof sq.detail_answer === 'string' ? sq.detail_answer : (sq.detail_answer != null ? String(sq.detail_answer) : '')}
                  onChange={(value) => handleSubQuestionChange(index, 'detail_answer', value)}
                  label="Detail Answer (optional)"
                  placeholder="Enter detailed answer / working steps..."
                  minRows={2}
                />

                <OptionalImageField
                  label="Detail Answer Image (optional)"
                  previewUrl={newDetailImagePreview[index] || sq.detail_answer_image_proxy_url || sq.detail_answer_image || ''}
                  onClear={() => handleClearDetailImage(index)}
                  onFileSelect={(file) => handleDetailImageChange(index, file)}
                  onPaste={(file) => handleDetailImageChange(index, file)}
                  minHeight={120}
                  maxImageHeight={150}
                  inputId={`subquestion-detail-answer-image-${index}`}
                  emptyInstruction="Click, paste (Ctrl+V), or attach detail answer image here"
                  imageAlt={`sub-question-${index}-detail-answer`}
                />
                
                <TextField
                  type="number"
                  label="Marks"
                  value={sq.marks || 1}
                  onChange={(e) => handleSubQuestionChange(index, 'marks', parseInt(e.target.value) || 1)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Stack>
            </Paper>
          ))}
          
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddSubQuestion}
            sx={{ mt: 1 }}
          >
            Add Sub-Question
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Done</Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
        >
          Save & Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubQuestionDialog;
